import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import type { Client } from '@ooh/contracts';

interface ClientArchiveConfirmProps {
  client: Client;
  onCancel: () => void;
  onConfirm: () => void;
  isPending?: boolean;
}

export function ClientArchiveConfirm({
  client,
  onCancel,
  onConfirm,
  isPending = false,
}: ClientArchiveConfirmProps) {
  const { t } = useTranslation('clients');

  return (
    <ModalDialog
      testId="client-archive-confirm"
      onClose={onCancel}
      closeLabel={t('actions.cancel')}
      header={
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-full bg-destructive/10 shrink-0 mt-0.5">
            <AlertTriangle className="w-4 h-4 text-destructive" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">{t('dialog.archiveTitle')}</h2>
            <p className="text-sm text-muted-foreground mt-1">{client.name}</p>
          </div>
        </div>
      }
      footer={
        <>
          <Button variant="outline" onClick={onCancel} data-testid="cancelArchiveButton">
            {t('actions.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="confirmArchiveButton"
          >
            {t('actions.archive')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted-foreground">{t('dialog.archiveDescription')}</p>
    </ModalDialog>
  );
}
